import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Moment } from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { getDatesRange, getTimezone } from '@mgm/common/utils';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/useAppStore';
import { getAuthenticateUser } from '../../../../../store/authenticate/selectors';
import { TasklistsController } from '../../../../../services';
import {
  IBaseResponse,
  TAddTasklistRes,
  TGetDailySubmissions,
  TGetSubmissionsRes,
  TGetTasklistsRes,
} from '../../../../../types/responses';
import { tasklistsActions } from '../../../../../store/tasklists/slice';
import { QUERY_KEYS } from '../../../../../utils/enums';
import { TSubmission } from '../../../../../types';
import { TSubmitTasklistReq, TUpdateSubmissionReq } from '../../../../../types/requests';
import { INITIAL_TASKLIST } from '../../data';
import { getTasklists } from '../../../../../store/tasklists/selectors';
import { useError, usePermissions } from '../../../../../hooks/useCommon';

export function useGetTasklistsAPI(isMainTasklistsPage = false) {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  return useQuery(
    [QUERY_KEYS.TASKLISTS],
    async () => {
      const data = {
        shopName: personnel.shopName,
      };

      const rsp = await TasklistsController().getTasklists<TGetTasklistsRes>(data);

      if (rsp?.success && 'tasklists' in rsp) {
        dispatch(tasklistsActions.setTasklists([...rsp.tasklists]));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get tasklists failed! ${rsp.msg}`);
      }
      return rsp as TGetTasklistsRes;
    },
    { staleTime: 3 * (60 * 1000), suspense: false, enabled: isMainTasklistsPage },
  );
}

type TUseGetSubmissionsAPIOptions = {
  isMainSubmissionsPage?: boolean
  dates?: [Moment, Moment],
};

export function useGetSubmissionsAPI(options: TUseGetSubmissionsAPIOptions) {
  const { fromDate, toDate } = getDatesRange(options?.dates);
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { permissions } = usePermissions();
  const { handleReqError } = useError();

  const locationIds = permissions.isManager ? personnel.locations.map(({ id }) => id) : [];

  return useQuery(
    [QUERY_KEYS.SUBMISSIONS],
    async () => {
      const data = {
        shopName: personnel.shopName,
        fromDate,
        toDate,
        timezone: getTimezone(),
        locationIds,
      };

      const rsp = await TasklistsController().getSubmissions<TGetSubmissionsRes>(data);

      if (rsp?.success && 'submissions' in rsp) {
        const { summary, submittedTasklists } = rsp.submissions ?? {};

        dispatch(tasklistsActions.setSubmissions({ ...summary }));
        dispatch(tasklistsActions.setSubmittedTasklists(submittedTasklists || []));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get submissions failed! ${rsp.msg}`);
      }

      return rsp as TGetSubmissionsRes;
    },
    { staleTime: 3 * (60 * 1000), suspense: false, enabled: options.isMainSubmissionsPage || false },
  );
}

export function useGetDailySubmissionsAPI(isMainTasklistsPage = false) {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  return useQuery(
    [QUERY_KEYS.DAILY_SUBMISSIONS],
    async () => {
      const data = {
        shopName: personnel.shopName,
        timezone: getTimezone(),
      };

      const rsp = await TasklistsController().dailySubmissions<TGetDailySubmissions>(data);

      if (rsp?.success && 'submissions' in rsp) {
        dispatch(tasklistsActions.setDailySubmissions(rsp.submissions));

        sessionStorage.setItem('prev', JSON.stringify(rsp.submissions));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get daily submissions failed! ${rsp.msg}`);
      }
      return rsp as TGetDailySubmissions;
    },
    { staleTime: 3 * (60 * 1000), suspense: false, enabled: isMainTasklistsPage },
  );
}

export function useDeleteTasklistAPI() {
  const { enqueueSnackbar } = useSnackbar();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const tasklists = useAppSelector(getTasklists);
  const { t } = useTranslation('tasklists');
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  const deleteTasklistApi = (tasklistId: number) => {
    const data = {
      tasklistId,
      shopName: personnel.shopName,
    };
    return TasklistsController().deleteTasklist<IBaseResponse>(data);
  };

  return useMutation(deleteTasklistApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        const updatedTasklists = tasklists.filter((tasklist) => tasklist.id !== variables);

        dispatch(tasklistsActions.setTasklists([...updatedTasklists]));

        enqueueSnackbar(`${t('tasklistWasDeleted')}`, {
          variant: 'success',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! delete tasklist failed! ${rsp.msg}`);
      }
    },
    onError: (err) => {
      console.error(`ERROR! delete tasklist request threw an Exception! ${err}`);
    },
  });
}

export function useEditTasklistAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { refetch: refetchTasklists } = useGetTasklistsAPI();
  const { handleReqError } = useError();

  const editTasklistApi = ({ tasklistData, tasklistId }: {
    tasklistData: typeof INITIAL_TASKLIST,
    tasklistId: number
  }) => {
    const data = {
      tasklistId,
      notifyAssignee: tasklistData.notifyAssignee,
      tasklist: {
        title: tasklistData.name,
        tasks: tasklistData.tasks,
        description: tasklistData.description,
        isDated: tasklistData.isDated,
        isAssigned: !!tasklistData.assign.email,
        locations: tasklistData.locations,
        visibility: tasklistData.visibility.map((item) => item.id),
        date: tasklistData.date,
        creator: {
          email: personnel.email,
          avatarFile: personnel.avatarFile || '',
          name: personnel.name,
        },
        assigneeAvatar: tasklistData.assign.avatarFile,
        assigneeEmail: tasklistData.assign.email,
        assigneeName: tasklistData.assign.name,
        shopName: personnel.shopName,
      },
    };
    return TasklistsController().updateTasklist<IBaseResponse>(data);
  };

  return useMutation(editTasklistApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await refetchTasklists();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update tasklist failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! update tasklist request threw an Exception! ${err}`);
    },
  });
}

export function useAddTasklistAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { refetch: refetchTasklists } = useGetTasklistsAPI();
  const { handleReqError } = useError();
  const { t } = useTranslation('tasklists');

  const { enqueueSnackbar } = useSnackbar();

  const addTasklistApi = (tasklist: typeof INITIAL_TASKLIST) => {
    const data = {
      notifyAssignee: tasklist.notifyAssignee,
      tasklist: {
        title: tasklist.name,
        tasks: tasklist.tasks,
        description: tasklist.description,
        isDated: tasklist.isDated,
        isAssigned: !!tasklist.assign.email,
        locations: tasklist.locations,
        visibility: tasklist.visibility.map((item) => item.id),
        date: tasklist.date,
        creator: {
          email: personnel.email,
          avatarFile: personnel.avatarFile || '',
          name: personnel.name,
        },
        assigneeAvatar: tasklist.assign.avatarFile,
        assigneeEmail: tasklist.assign.email,
        assigneeName: tasklist.assign.name,
        shopName: personnel.shopName,
      },
    };

    return TasklistsController().addTasklist<TAddTasklistRes>(data);
  };

  return useMutation(addTasklistApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        enqueueSnackbar(`${t('tasklistWasAdded')}`, {
          variant: 'success',
        });
        await refetchTasklists();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! add tasklist failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! add tasklist request threw an Exception! ${err}`);
    },
  });
}

export function useSubmitTasklistAPI() {
  const { refetch: refetchSubmissions } = useGetSubmissionsAPI({ isMainSubmissionsPage: false });
  const { refetch: refetchDaily } = useGetDailySubmissionsAPI();
  const { t } = useTranslation('tasklists');

  const { enqueueSnackbar } = useSnackbar();

  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();

  const submitTasklistApi = (submission: TSubmitTasklistReq) => {
    const data = {
      submission: {
        ...submission,
        shopName: personnel.shopName,
        timezone: getTimezone(),
        submitterEmail: personnel.email,
        submitterName: personnel.name,
        avatarFile: personnel.avatarFile,
      } as TSubmission,
    };

    return TasklistsController().submitTasklist<IBaseResponse>(data);
  };

  return useMutation(submitTasklistApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        enqueueSnackbar(`${t('tasklistWasSubmitted')}`, {
          variant: 'success',
        });
        await refetchSubmissions();
        await refetchDaily();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! submit tasklist failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! submit tasklist request threw an Exception! ${err}`);
    },
  });
}

export function useUpdateSubmissionAPI() {
  const { refetch: refetchSubmissions } = useGetSubmissionsAPI({ isMainSubmissionsPage: false });
  const { refetch: refetchDaily } = useGetDailySubmissionsAPI();
  const { t } = useTranslation('tasklists');

  const { enqueueSnackbar } = useSnackbar();

  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();

  const updateSubmissionApi = (submission: Omit<TUpdateSubmissionReq, 'shopName'>) => {
    const data = {
      ...submission,
      shopName: personnel.shopName,
    };

    return TasklistsController().updateSubmission<IBaseResponse>(data);
  };

  return useMutation(updateSubmissionApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        enqueueSnackbar(`${t('submissionWasUpdated')}`, {
          variant: 'success',
        });
        await refetchSubmissions();
        await refetchDaily();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update submission failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! update submission request threw an Exception! ${err}`);
    },
  });
}
